/*
  Andon TV dashboard — cast to a large factory screen. Shows live per-machine
  OEE + status + top downtime reasons. Auto-refreshes every 30 seconds.
  Rendered OUTSIDE AppLayout, at route /andon.
*/
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Cpu, Activity, AlertTriangle, Zap, TrendingUp, LogOut } from "lucide-react";

const REFRESH_MS = 30_000;

function gradeColor(v) { return v >= 85 ? "text-emerald-400" : v >= 60 ? "text-yellow-300" : "text-red-400"; }
function bgTint(v) {
  return v >= 85 ? "from-emerald-500/20 to-emerald-800/10 border-emerald-500/40"
       : v >= 60 ? "from-yellow-500/20 to-yellow-800/10 border-yellow-500/40"
       : "from-red-500/20 to-red-800/10 border-red-500/40 animate-pulse";
}

export default function Andon() {
  const [data, setData] = useState(null);
  const [downtime, setDowntime] = useState([]);
  const [tick, setTick] = useState(0);
  const [offline, setOffline] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  const load = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const start = new Date(); start.setDate(start.getDate() - 7);
    const from_date = start.toISOString().slice(0, 10);
    const params = { from_date, to_date: today };
    if (branchId) params.branch_id = branchId;
    try {
      const [oee, dt] = await Promise.all([
        api.get("/production/oee/summary", { params }),
        api.get("/production/downtime", { params }),
      ]);
      setData(oee.data);
      setOffline(false);
      // Aggregate downtime by reason
      const byReason = {};
      dt.data.forEach((d) => {
        byReason[d.reason] = (byReason[d.reason] || 0) + Number(d.duration_min || 0);
      });
      const top = Object.entries(byReason).sort((a, b) => b[1] - a[1]).slice(0, 5);
      setDowntime(top);
    } catch (e) { console.error(e); setOffline(true); }
  };
  useEffect(() => {
    // Reset stale data immediately so the operator doesn't see the wrong plant briefly
    setData(null); setDowntime([]);
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") { load(); setTick((x) => x + 1); }
    }, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [branchId]);

  const clock = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div className="fixed inset-0 bg-neutral-950 text-white font-sans overflow-hidden" data-testid="andon-tv">
      {/* Header */}
      <div className="h-16 border-b border-white/10 flex items-center px-8 gap-6 bg-black">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// ANDON · Zenith 360</div>
        <div className="text-2xl font-medium">Factory Live Board</div>
        {branches.length > 1 && (
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                   className="bg-white/5 border border-white/20 text-xs px-3 py-1.5 rounded-sm font-mono uppercase text-white/80"
                   data-testid="andon-plant-selector">
            <option value="">All plants</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
          </select>
        )}
        {offline && <div className="text-[10px] font-mono uppercase text-red-400 border border-red-500/40 px-2 py-0.5 animate-pulse" data-testid="andon-offline">// signal lost — data may be stale</div>}
        <div className="flex-1" />
        <div className="tabular-nums text-2xl" data-testid="andon-clock">{clock}</div>
        <a href="/app/dashboard" className="text-white/40 hover:text-white text-xs flex items-center gap-1"><LogOut className="w-3 h-3" /> Exit</a>
      </div>

      {/* KPI Row */}
      {data && (
        <div className="grid grid-cols-4 gap-4 p-6">
          <div className="border-2 border-white/10 p-6 bg-gradient-to-br from-white/5 to-transparent">
            <div className="text-[10px] font-mono uppercase text-white/50">Plant OEE (7-day)</div>
            <div className={`text-7xl font-medium tabular-nums ${gradeColor(data.avg_oee)}`} data-testid="andon-oee">{data.avg_oee}%</div>
            <div className="text-xs text-white/40 mt-2">world-class ≥ 85%</div>
          </div>
          <div className="border-2 border-blue-500/40 p-6 bg-gradient-to-br from-blue-500/10 to-transparent">
            <div className="text-[10px] font-mono uppercase text-blue-300">Availability</div>
            <div className="text-5xl font-medium tabular-nums text-blue-300">{data.avg_availability}%</div>
          </div>
          <div className="border-2 border-yellow-500/40 p-6 bg-gradient-to-br from-yellow-500/10 to-transparent">
            <div className="text-[10px] font-mono uppercase text-yellow-300">Performance</div>
            <div className="text-5xl font-medium tabular-nums text-yellow-300">{data.avg_performance}%</div>
          </div>
          <div className="border-2 border-emerald-500/40 p-6 bg-gradient-to-br from-emerald-500/10 to-transparent">
            <div className="text-[10px] font-mono uppercase text-emerald-300">Quality</div>
            <div className="text-5xl font-medium tabular-nums text-emerald-300">{data.avg_quality}%</div>
          </div>
        </div>
      )}

      {/* Machine grid + Downtime */}
      <div className="grid grid-cols-4 gap-4 px-6 pb-6 h-[calc(100vh-15rem)]">
        <div className="col-span-3 grid grid-cols-3 md:grid-cols-4 gap-3 overflow-y-auto">
          {(data?.machines || []).map((m) => (
            <div key={m.machine_id} className={`border-2 p-4 bg-gradient-to-br ${bgTint(m.oee)}`} data-testid={`andon-mc-${m.machine_code}`}>
              <div className="flex items-center gap-2">
                <Cpu className={`w-5 h-5 ${gradeColor(m.oee)}`} />
                <div className="font-mono text-lg">{m.machine_code}</div>
              </div>
              <div className="text-xs text-white/60 truncate">{m.machine_name}</div>
              <div className={`text-4xl font-medium tabular-nums mt-3 ${gradeColor(m.oee)}`}>{m.oee}%</div>
              <div className="flex justify-between text-[10px] font-mono text-white/40 mt-2">
                <span>A {m.availability}%</span>
                <span>P {m.performance}%</span>
                <span>Q {m.quality}%</span>
              </div>
              {m.downtime_min > 0 && (
                <div className="mt-2 text-[10px] font-mono flex items-center gap-1 text-red-400">
                  <AlertTriangle className="w-3 h-3" /> {Math.round(m.downtime_min)} min down
                </div>
              )}
            </div>
          ))}
          {(data?.machines || []).length === 0 && <div className="col-span-full text-white/40 text-center py-16 border-2 border-dashed border-white/10">No machine activity in the last 7 days.</div>}
        </div>

        {/* Downtime top-5 */}
        <div className="border-2 border-red-500/30 p-4 bg-gradient-to-br from-red-500/10 to-transparent" data-testid="andon-downtime">
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Top Downtime Causes (7-day)</div>
          <div className="mt-3 space-y-3">
            {downtime.length === 0 && <div className="text-white/40 text-sm">No downtime logged. 🎉</div>}
            {downtime.map(([reason, minutes], i) => {
              const total = downtime.reduce((s, [, m]) => s + m, 0) || 1;
              const pct = (minutes / total) * 100;
              return (
                <div key={reason} data-testid={`andon-dt-${reason}`}>
                  <div className="flex justify-between text-xs">
                    <span className="uppercase font-mono text-white/80">{i + 1}. {reason.replace(/_/g, " ")}</span>
                    <span className="tabular-nums">{Math.round(minutes)} min</span>
                  </div>
                  <div className="h-2 bg-white/10 mt-1"><div className="h-full bg-red-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] font-mono text-white/30 mt-6 text-right">Auto-refresh · {tick}</div>
        </div>
      </div>
    </div>
  );
}
